import React, { useState, useMemo, useEffect, useRef, useCallback, useLayoutEffect, startTransition } from 'react';
import ReactDOM from 'react-dom';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import AppointmentCard from './AppointmentCard';

/**
 * Full View Component - 21-Day Scrollable Timeline
 *
 * A continuous horizontally-scrollable grid showing ~21 days at once.
 * Key differences from WeekView:
 * - 21 days instead of 7 (centered on currentDate ±10 days)
 * - Fixed pixel-width day columns (not fr units) for predictable scroll math
 * - Sticky time column (left: 0) so time labels stay visible while scrolling
 * - Infinite horizontal scroll: approaching edges triggers buffer shift
 * - Dynamic date range header showing currently-visible dates
 * - Nav arrows smooth-scroll by ~7 days
 * - Same drag-and-drop, zoom, and overlap systems from WeekView
 */
const FullView2 = ({ currentDate, appointments, onAppointmentClick, onTimeSlotClick, onNavigate, onAppointmentDrop, onQuickStatus }) => {
  const [draggedAppointment, setDraggedAppointment] = useState(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [resizingAppointment, setResizingAppointment] = useState(null);
  const resizeRef = useRef({ startY: 0, startDuration: 0, currentDuration: 0 });
  const resizeLabelRef = useRef(null); // DOM ref for duration label during resize
  const resizeCardRef = useRef(null);  // DOM ref for the card wrapper being resized
  const resizePointerRef = useRef(null); // Pointer ID for capture
  const resizeHandleRef = useRef(null);  // DOM ref to resize handle element
  const resizeJustFinishedRef = useRef(false); // Suppress click after resize
  const [dragImageElement, setDragImageElement] = useState(null);
  const visibleDateRangeRef = useRef(null); // DOM ref for direct text update (no re-render on scroll)

  // Touch drag state refs
  const touchDragRef = useRef(null);
  const autoScrollRef = useRef(null);
  const fullViewContainerRef = useRef(null);
  const touchDragSlotRef = useRef(null);
  const desktopDragRef = useRef(false);
  const dragOffsetRef = useRef(0);
  const desktopDragSlotRef = useRef(null);
  const draggedAppointmentRef = useRef(null);
  const initialScrollDone = useRef(false);
  const verticalScrollDone = useRef(false);
  const bufferShiftInProgress = useRef(false);
  // (navDirectionRef removed — buffer extensions are handled by prevBufferLeftRef)
  const prevCurrentDateRef = useRef(currentDate.getTime());
  const prevDayColumnWidthsRef = useRef(null); // For scroll restoration with variable widths
  const zoomAnchorRef = useRef(null); // { contentX, contentY, viewX, viewY } — cursor-anchored zoom
  const prevZoomRef = useRef(null); // { slotHeight, dayColWidth, timeColWidth } — for zoom scroll restoration
  const dragLabelRef = useRef(null); // DOM ref for date/time label during card drag

  // Column virtualization handled by CSS content-visibility: auto (no JS state needed)

  // Drag preview — positioned via direct DOM manipulation (zero React re-renders during drag)
  const dragPreviewRef = useRef(null);
  const dayColumnWidthsRef = useRef(null);
  const dayColWidthRef = useRef(null);
  const timeColWidthRef = useRef(null);
  const calculateOverlapColumnsRef = useRef(null);
  const zoomLevelRef = useRef(zoomLevel);
  zoomLevelRef.current = zoomLevel;

  // Refs for mount-once pointer event listeners
  const onAppointmentDropRef = useRef(onAppointmentDrop);
  const onNavigateRef = useRef(onNavigate);
  const slotHeightRef = useRef(null);
  const startHourRef = useRef(null);
  const endHourRef = useRef(null);
  const allDaysRef = useRef(null);
  useEffect(() => { onAppointmentDropRef.current = onAppointmentDrop; }, [onAppointmentDrop]);
  useEffect(() => { onNavigateRef.current = onNavigate; }, [onNavigate]);

  // Overflow popover state
  const [overflowPopover, setOverflowPopover] = useState(null);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Zoom configuration - fixed pixel widths for predictable scroll
  const zoomLevels = {
    0.5: { name: 'Tiny', slotHeight: 20, timeColWidth: 40, dayColWidth: 80 },
    0.75: { name: 'Compact', slotHeight: 30, timeColWidth: 50, dayColWidth: 100 },
    1: { name: 'Normal', slotHeight: 45, timeColWidth: 60, dayColWidth: 120 },
    1.5: { name: 'Comfortable', slotHeight: 60, timeColWidth: 80, dayColWidth: 180 },
    2: { name: 'Detailed', slotHeight: 90, timeColWidth: 100, dayColWidth: 260 }
  };

  const currentZoom = zoomLevels[zoomLevel];
  const slotHeight = currentZoom.slotHeight;
  const dayColWidth = currentZoom.dayColWidth;
  const timeColWidth = currentZoom.timeColWidth;
  slotHeightRef.current = slotHeight;
  dayColWidthRef.current = dayColWidth;
  timeColWidthRef.current = timeColWidth;

  // Zoom controls — capture cursor anchor before changing level
  const captureZoomAnchor = (clientX, clientY) => {
    const container = fullViewContainerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    // Viewport-relative cursor position (default to center)
    const viewX = (clientX != null) ? clientX - rect.left : rect.width / 2;
    const viewY = (clientY != null) ? clientY - rect.top : rect.height / 2;
    // Content position under cursor
    const contentX = container.scrollLeft + viewX;
    const contentY = container.scrollTop + viewY;
    zoomAnchorRef.current = { contentX, contentY, viewX, viewY };
    // Read current zoom values from refs (not closure) so this works in stale useEffect closures
    prevZoomRef.current = {
      slotHeight: slotHeightRef.current,
      dayColWidth: dayColWidthRef.current,
      timeColWidth: timeColWidthRef.current
    };
  };

  const zoomLevelOrder = [0.5, 0.75, 1, 1.5, 2];

  const handleZoomIn = (clientX, clientY) => {
    const cur = zoomLevelRef.current;
    const idx = zoomLevelOrder.indexOf(cur);
    if (idx >= zoomLevelOrder.length - 1) return;
    captureZoomAnchor(clientX, clientY);
    setZoomLevel(zoomLevelOrder[idx + 1]);
  };

  const handleZoomOut = (clientX, clientY) => {
    const cur = zoomLevelRef.current;
    const idx = zoomLevelOrder.indexOf(cur);
    if (idx <= 0) return;
    captureZoomAnchor(clientX, clientY);
    setZoomLevel(zoomLevelOrder[idx - 1]);
  };

  const handleResetZoom = (clientX, clientY) => {
    if (zoomLevelRef.current === 1) return;
    captureZoomAnchor(clientX, clientY);
    setZoomLevel(1);
  };

  // Keyboard zoom shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === '=' || e.key === '+') {
          e.preventDefault();
          handleZoomIn(null, null);
        } else if (e.key === '-' || e.key === '_') {
          e.preventDefault();
          handleZoomOut(null, null);
        } else if (e.key === '0') {
          e.preventDefault();
          handleResetZoom(null, null);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Mouse wheel zoom (Ctrl + Scroll) and pinch-to-zoom
  // Accumulate small deltas so pinch gestures need a bigger movement to trigger a zoom step
  const zoomDeltaRef = useRef(0);
  const ZOOM_THRESHOLD = 30; // accumulated deltaY needed per zoom step

  useEffect(() => {
    const handleWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        zoomDeltaRef.current += e.deltaY;
        if (zoomDeltaRef.current <= -ZOOM_THRESHOLD) {
          zoomDeltaRef.current = 0;
          handleZoomIn(e.clientX, e.clientY);
        } else if (zoomDeltaRef.current >= ZOOM_THRESHOLD) {
          zoomDeltaRef.current = 0;
          handleZoomOut(e.clientX, e.clientY);
        }
      }
    };
    const container = fullViewContainerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }
  }, []);

  // Touch pinch-to-zoom (two-finger gesture)
  const pinchRef = useRef({ active: false, startDist: 0 });
  const PINCH_THRESHOLD = 60; // px distance change needed to trigger zoom step

  useEffect(() => {
    const container = fullViewContainerRef.current;
    if (!container) return;

    const getDist = (t) => {
      const dx = t[0].clientX - t[1].clientX;
      const dy = t[0].clientY - t[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const getMid = (t) => ({
      x: (t[0].clientX + t[1].clientX) / 2,
      y: (t[0].clientY + t[1].clientY) / 2
    });

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        pinchRef.current = { active: true, startDist: getDist(e.touches) };
      }
    };

    const onTouchMove = (e) => {
      if (!pinchRef.current.active || e.touches.length !== 2) return;
      e.preventDefault(); // prevent native browser zoom
      const dist = getDist(e.touches);
      const delta = dist - pinchRef.current.startDist;
      if (delta > PINCH_THRESHOLD) {
        pinchRef.current.startDist = dist;
        const mid = getMid(e.touches);
        handleZoomIn(mid.x, mid.y);
      } else if (delta < -PINCH_THRESHOLD) {
        pinchRef.current.startDist = dist;
        const mid = getMid(e.touches);
        handleZoomOut(mid.x, mid.y);
      }
    };

    const onTouchEnd = () => {
      pinchRef.current.active = false;
    };

    container.addEventListener('touchstart', onTouchStart, { passive: true });
    container.addEventListener('touchmove', onTouchMove, { passive: false });
    container.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  // Buffer geometry constants
  // ±30 days initially. When user scrolls within 50% of an edge, extend
  // by 14 days in the background (startTransition) — triggers early so
  // new days render well before the user reaches the edge.
  const INITIAL_HALF = 30;
  const EXTEND_DAYS = 14;
  const EDGE_FRACTION = 0.35; // trigger when 35% of content remains on that side

  // Internal buffer origin — stays fixed; we extend left/right if needed
  const [bufferRange, setBufferRange] = useState(() => {
    const anchor = new Date(currentDate);
    anchor.setHours(12, 0, 0, 0);
    return { anchorTime: anchor.getTime(), left: INITIAL_HALF, right: INITIAL_HALF };
  });

  /**
   * Generate days from buffer range.  The array is stable as long as
   * bufferRange stays the same — no jitter on currentDate prop changes.
   */
  const allDays = useMemo(() => {
    const anchor = new Date(bufferRange.anchorTime);
    const days = [];
    for (let i = -bufferRange.left; i <= bufferRange.right; i++) {
      const day = new Date(anchor);
      day.setDate(anchor.getDate() + i);
      day.setHours(12, 0, 0, 0);
      days.push(day);
    }
    return days;
  }, [bufferRange]);

  // BUFFER_HALF = index of anchor date in allDays
  const BUFFER_HALF = bufferRange.left;

  allDaysRef.current = allDays;

  // Full 24-hour grid
  const startHour = 0;
  const endHour = 24;

  startHourRef.current = startHour;
  endHourRef.current = endHour;

  // 15-minute time slots
  const timeSlots = useMemo(() => {
    const slots = [];
    for (let hour = startHour; hour < endHour; hour++) {
      slots.push({ hour, minute: 0 });
      slots.push({ hour, minute: 15 });
      slots.push({ hour, minute: 30 });
      slots.push({ hour, minute: 45 });
    }
    return slots;
  }, [startHour, endHour]);

  // Pre-parsed appointment data — parse dates ONCE, index by date and slot.
  // This eliminates thousands of repeated new Date() calls in useMemos and render.
  const { appointmentsByDate, appointmentsBySlot, parsedAppointments } = useMemo(() => {
    const byDate = new Map();  // "YYYY-MM-DD" → [{ apt, start, end, dateStr, hour, minute }]
    const bySlot = new Map();  // "YYYY-MM-DD-H-M" → [apt]
    const parsed = new Map();  // apt.id → { start, end, dateStr, hour, minute }

    appointments.forEach(apt => {
      const start = new Date(apt.scheduled_date);
      const duration = apt.estimated_duration || 60;
      const end = new Date(start.getTime() + duration * 60000);
      const dateStr = start.toISOString().split('T')[0];
      const hour = start.getHours();
      const minute = start.getMinutes();

      const info = { apt, start, end, dateStr, hour, minute };
      parsed.set(apt.id, info);

      if (!byDate.has(dateStr)) byDate.set(dateStr, []);
      byDate.get(dateStr).push(info);

      const slotKey = `${dateStr}-${hour}-${minute}`;
      if (!bySlot.has(slotKey)) bySlot.set(slotKey, []);
      bySlot.get(slotKey).push(apt);
    });

    return { appointmentsByDate: byDate, appointmentsBySlot: bySlot, parsedAppointments: parsed };
  }, [appointments]);

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isCurrentDate = (date) => {
    return date.toDateString() === currentDate.toDateString();
  };

  /**
   * Overlap columns calculation — only processes days that have appointments.
   * Uses pre-parsed date data to avoid repeated new Date() calls.
   */
  const calculateOverlapColumns = useMemo(() => {
    const slotData = new Map();

    allDays.forEach(day => {
      const dateStr = day.toISOString().split('T')[0];
      const dayApts = appointmentsByDate.get(dateStr);
      if (!dayApts || dayApts.length === 0) return; // Skip empty days entirely

      const dayISO = day.toISOString();
      // Compute midnight timestamp once per day (avoid new Date() per slot)
      const midnightMs = new Date(`${dateStr}T00:00:00`).getTime();

      timeSlots.forEach(({ hour, minute }) => {
        const slotStartMs = midnightMs + (hour * 3600000) + (minute * 60000);
        const slotEndMs = slotStartMs + 15 * 60000;

        const startingApts = dayApts
          .filter(info => info.hour === hour && info.minute === minute)
          .map(info => info.apt);

        const overlapping = dayApts
          .filter(info => info.start.getTime() < slotEndMs && info.end.getTime() > slotStartMs);

        if (overlapping.length === 0 && startingApts.length === 0) return;

        const appointmentColumns = new Map();
        const columnEndTimes = [];

        overlapping
          .sort((a, b) => a.start - b.start)
          .forEach(info => {
            let col = columnEndTimes.findIndex(endTime => endTime <= info.start);
            if (col === -1) {
              col = columnEndTimes.length;
              columnEndTimes.push(info.end);
            } else {
              columnEndTimes[col] = info.end;
            }
            appointmentColumns.set(info.apt.id, col);
          });

        const slotKey = `${dayISO}-${hour}-${minute}`;
        slotData.set(slotKey, {
          columns: Math.max(1, columnEndTimes.length),
          appointmentColumns,
          appointments: startingApts
        });
      });
    });

    return slotData;
  }, [appointmentsByDate, allDays, timeSlots]);
  calculateOverlapColumnsRef.current = calculateOverlapColumns;

  /**
   * Global per-day column layout — assigns each appointment a consistent
   * column index across its entire duration by finding overlap groups
   * (connected components of transitively overlapping appointments)
   * and assigning columns within each group.
   *
   * This fixes the bug where per-slot column assignment gave inconsistent
   * indices to appointments that overlap in chains (A↔B↔C but not A↔C).
   */
  const globalColumnLayout = useMemo(() => {
    const layout = new Map(); // aptId -> { colIndex, totalCols }

    allDays.forEach(day => {
      const dateStr = day.toISOString().split('T')[0];
      const dayInfos = appointmentsByDate.get(dateStr);
      if (!dayInfos || dayInfos.length === 0) return; // Skip empty days

      // Sort by pre-parsed start time, then by duration (longer first)
      const sorted = [...dayInfos].sort((a, b) => {
        const diff = a.start - b.start;
        if (diff !== 0) return diff;
        return (b.apt.estimated_duration || 60) - (a.apt.estimated_duration || 60);
      });

      // Helper to check if two parsed infos overlap (uses pre-parsed start/end)
      const overlaps = (info1, info2) => {
        return info1.start < info2.end && info2.start < info1.end;
      };

      // Find overlap groups (connected components via BFS)
      const visited = new Set();
      const groups = [];

      sorted.forEach(info => {
        if (visited.has(info.apt.id)) return;

        const group = [info];
        visited.add(info.apt.id);
        const queue = [info];

        while (queue.length > 0) {
          const current = queue.shift();
          sorted.forEach(other => {
            if (!visited.has(other.apt.id) && overlaps(current, other)) {
              visited.add(other.apt.id);
              group.push(other);
              queue.push(other);
            }
          });
        }

        groups.push(group);
      });

      // Assign columns within each group greedily
      groups.forEach(group => {
        group.sort((a, b) => {
          const diff = a.start - b.start;
          if (diff !== 0) return diff;
          return (b.apt.estimated_duration || 60) - (a.apt.estimated_duration || 60);
        });

        const columnEndTimes = [];

        group.forEach(info => {
          let col = columnEndTimes.findIndex(endTime => endTime <= info.start);
          if (col === -1) {
            col = columnEndTimes.length;
            columnEndTimes.push(info.end);
          } else {
            columnEndTimes[col] = info.end;
          }

          layout.set(info.apt.id, { colIndex: col, totalCols: 0 });
        });

        const totalCols = columnEndTimes.length;
        group.forEach(info => {
          layout.get(info.apt.id).totalCols = totalCols;
        });
      });
    });

    return layout;
  }, [appointmentsByDate, allDays]);

  /**
   * Per-day max overlap columns — days with overlapping appointments get wider
   */
  const dayOverlapCounts = useMemo(() => {
    const counts = new Map();
    allDays.forEach((day, dayIndex) => {
      const dateStr = day.toISOString().split('T')[0];
      const dayInfos = appointmentsByDate.get(dateStr);
      if (!dayInfos || dayInfos.length === 0) {
        counts.set(dayIndex, 1);
        return;
      }
      let maxCols = 1;
      dayInfos.forEach(info => {
        const layoutInfo = globalColumnLayout.get(info.apt.id);
        if (layoutInfo && layoutInfo.totalCols > maxCols) {
          maxCols = layoutInfo.totalCols;
        }
      });
      counts.set(dayIndex, maxCols);
    });
    return counts;
  }, [globalColumnLayout, appointmentsByDate, allDays]);

  /**
   * Per-day column widths (base width × overlap count) and cumulative offsets.
   * Days with overlapping appointments expand proportionally.
   */
  const { dayColumnWidths, dayColumnOffsets, contentWidth } = useMemo(() => {
    const widths = allDays.map((_, i) => {
      const overlap = dayOverlapCounts.get(i) || 1;
      return dayColWidth * overlap;
    });
    const offsets = [0];
    for (let i = 0; i < widths.length; i++) {
      offsets.push(offsets[i] + widths[i]);
    }
    const total = timeColWidth + offsets[offsets.length - 1];
    return { dayColumnWidths: widths, dayColumnOffsets: offsets, contentWidth: total };
  }, [allDays, dayOverlapCounts, dayColWidth, timeColWidth]);
  dayColumnWidthsRef.current = dayColumnWidths;

  /**
   * Calculate which dates are currently visible in the viewport
   */
  // Ref for dayColumnOffsets so scroll handler can access current values without re-binding
  const dayColumnOffsetsRef = useRef(dayColumnOffsets);
  dayColumnOffsetsRef.current = dayColumnOffsets;

  const updateVisibleDateRange = useCallback(() => {
    const container = fullViewContainerRef.current;
    if (!container || !allDaysRef.current) return;

    const scrollLeft = container.scrollLeft;
    const viewportWidth = container.clientWidth;
    const tw = timeColWidth;
    const days = allDaysRef.current;
    const offsets = dayColumnOffsetsRef.current;

    // Find first visible day
    let firstVisibleDayIndex = 0;
    const contentStart = scrollLeft - tw;
    for (let i = 0; i < days.length; i++) {
      if (offsets[i + 1] > contentStart) { firstVisibleDayIndex = i; break; }
    }
    let lastVisibleDayIndex = firstVisibleDayIndex;
    const contentEnd = scrollLeft - tw + viewportWidth;
    for (let i = firstVisibleDayIndex; i < days.length; i++) {
      if (offsets[i] >= contentEnd) break;
      lastVisibleDayIndex = i;
    }

    firstVisibleDayIndex = Math.max(0, firstVisibleDayIndex);
    lastVisibleDayIndex = Math.min(days.length - 1, lastVisibleDayIndex);

    // Update header text via direct DOM write (no re-render)
    if (firstVisibleDayIndex < days.length && lastVisibleDayIndex >= 0) {
      const firstDay = days[firstVisibleDayIndex];
      const lastDay = days[lastVisibleDayIndex];
      const formatOpts = { month: 'short', day: 'numeric' };

      let text;
      if (firstDay.getMonth() === lastDay.getMonth()) {
        text = `${firstDay.toLocaleDateString('en-US', formatOpts)} - ${lastDay.getDate()}, ${lastDay.getFullYear()}`;
      } else {
        text = `${firstDay.toLocaleDateString('en-US', formatOpts)} - ${lastDay.toLocaleDateString('en-US', { ...formatOpts, year: 'numeric' })}`;
      }
      if (visibleDateRangeRef.current) visibleDateRangeRef.current.textContent = text;
    }

  }, [timeColWidth]);

  /**
   * Infinite scroll detection — when user approaches left/right edges.
   *
   * Instead of shifting the buffer (which triggers expensive re-renders),
   * we extend it in the background via startTransition.  The allDays array
   * grows — new columns are added beyond the viewport edge — so the user
   * never sees a jank frame.
   */
  const extendingRef = useRef({ left: false, right: false });

  const handleScroll = useCallback(() => {
    const container = fullViewContainerRef.current;
    if (!container) return;

    updateVisibleDateRange();

    const scrollLeft = container.scrollLeft;
    const scrollRight = container.scrollWidth - container.clientWidth - scrollLeft;
    const totalScrollable = container.scrollWidth - container.clientWidth;
    const edgeThreshold = totalScrollable * EDGE_FRACTION;

    // Extend left — fires early and doesn't block right-side checks
    if (scrollLeft < edgeThreshold && !extendingRef.current.left) {
      extendingRef.current.left = true;
      bufferShiftInProgress.current = true;
      startTransition(() => {
        setBufferRange(prev => ({ ...prev, left: prev.left + EXTEND_DAYS }));
      });
    }

    // Extend right
    if (scrollRight < edgeThreshold && !extendingRef.current.right) {
      extendingRef.current.right = true;
      if (!bufferShiftInProgress.current) bufferShiftInProgress.current = true;
      startTransition(() => {
        setBufferRange(prev => ({ ...prev, right: prev.right + EXTEND_DAYS }));
      });
    }
  }, [updateVisibleDateRange]);

  /**
   * Scroll position restoration after buffer changes.
   *
   * Two cases:
   * 1. Buffer extended left: new days added at index 0 → scrollLeft must increase
   *    by their total width so the viewport stays on the same day.
   * 2. Buffer extended right / appointment moved / overlap change: preserve the
   *    anchor day at the viewport left edge using previous vs new widths.
   *
   * Runs in useLayoutEffect (before paint) to avoid any visual flicker.
   */
  const prevBufferRef = useRef({ left: bufferRange.left, right: bufferRange.right });

  useLayoutEffect(() => {
    const container = fullViewContainerRef.current;
    if (!container) return;

    const prev = prevBufferRef.current;
    const leftGrew = bufferRange.left > prev.left;
    const rightGrew = bufferRange.right > prev.right;
    prevBufferRef.current = { left: bufferRange.left, right: bufferRange.right };

    // Buffer extended left — new days inserted at start of allDays → adjust scroll
    if (leftGrew) {
      const addedCount = bufferRange.left - prev.left;
      const addedWidth = dayColumnWidths.slice(0, addedCount).reduce((s, w) => s + w, 0);
      container.scrollLeft += addedWidth;
      extendingRef.current.left = false;
      updateVisibleDateRange();
    }

    // Buffer extended right — no scroll adjustment needed
    if (rightGrew) {
      extendingRef.current.right = false;
    }

    if (leftGrew || rightGrew) {
      bufferShiftInProgress.current = false;
      prevDayColumnWidthsRef.current = dayColumnWidths;
      return;
    }

    // Detect non-buffer date changes (e.g. "Today" button)
    const prevTime = prevCurrentDateRef.current;
    const currTime = currentDate.getTime();
    if (prevTime !== currTime) {
      initialScrollDone.current = false;
      verticalScrollDone.current = false;
    }
    prevCurrentDateRef.current = currTime;

    // Initial scroll or re-centre: place currentDate (index BUFFER_HALF) in viewport centre
    if (!initialScrollDone.current) {
      const centerOffset = dayColumnOffsets[BUFFER_HALF];
      const currentDayWidth = dayColumnWidths[BUFFER_HALF] || dayColWidth;
      const centerScrollPos = timeColWidth + centerOffset - (container.clientWidth / 2) + (currentDayWidth / 2);
      container.scrollLeft = centerScrollPos;
      initialScrollDone.current = true;
      updateVisibleDateRange();
    } else if (zoomAnchorRef.current && prevZoomRef.current) {
      // Zoom changed — restore so the same day/time stays under the cursor
      const anchor = zoomAnchorRef.current;
      const oldZoom = prevZoomRef.current;
      zoomAnchorRef.current = null;
      prevZoomRef.current = null;

      // Scale horizontal: time column + day area
      const oldDayAreaX = anchor.contentX - oldZoom.timeColWidth;
      const hScale = dayColWidth / oldZoom.dayColWidth;
      const newContentX = timeColWidth + oldDayAreaX * hScale;
      container.scrollLeft = newContentX - anchor.viewX;

      // Scale vertical: header stays same height, slot area scales
      const headerEl = container.querySelector('.full-view-header');
      const headerHeight = headerEl ? headerEl.offsetHeight : 0;
      const oldSlotAreaY = anchor.contentY - headerHeight;
      const vScale = slotHeight / oldZoom.slotHeight;
      const newContentY = headerHeight + oldSlotAreaY * vScale;
      container.scrollTop = newContentY - anchor.viewY;
    } else {
      // Column widths changed (e.g. appointment moved, overlap count changed).
      // Preserve the user's viewport position.
      const prevWidths = prevDayColumnWidthsRef.current;
      if (prevWidths && prevWidths !== dayColumnWidths && prevWidths.length === dayColumnWidths.length) {
        const oldScrollLeft = container.scrollLeft;
        const contentX = oldScrollLeft - timeColWidth;
        let accum = 0;
        let anchorDay = 0;
        let offsetInDay = contentX;
        for (let i = 0; i < prevWidths.length; i++) {
          if (accum + prevWidths[i] > contentX) {
            anchorDay = i;
            offsetInDay = contentX - accum;
            offsetInDay = prevWidths[i] > 0 ? (offsetInDay / prevWidths[i]) * dayColumnWidths[i] : 0;
            break;
          }
          accum += prevWidths[i];
        }
        const newContentX = dayColumnOffsets[anchorDay] + offsetInDay;
        container.scrollLeft = timeColWidth + newContentX;
      }
    }

    prevDayColumnWidthsRef.current = dayColumnWidths;
  }, [allDays, dayColWidth, dayColumnWidths, dayColumnOffsets, timeColWidth, slotHeight, updateVisibleDateRange, currentDate, bufferRange]);

  // Vertical scroll: jump to current time or first appointment of today (once)
  useEffect(() => {
    if (verticalScrollDone.current) return;
    const container = fullViewContainerRef.current;
    if (!container || !initialScrollDone.current) return;

    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];
    let targetMinutes = now.getHours() * 60 + now.getMinutes();

    if (appointments && appointments.length > 0) {
      let earliestToday = Infinity;
      appointments.forEach(apt => {
        const aptDate = new Date(apt.scheduled_date);
        if (aptDate.toISOString().split('T')[0] === todayStr) {
          const aptMin = aptDate.getHours() * 60 + aptDate.getMinutes();
          if (aptMin < earliestToday) earliestToday = aptMin;
        }
      });
      if (earliestToday < targetMinutes && earliestToday !== Infinity) {
        targetMinutes = earliestToday;
      }
    }

    const headerEl = container.querySelector('.full-view-header');
    const headerHeight = headerEl ? headerEl.offsetHeight : 0;
    const targetSlot = (targetMinutes - startHour * 60) / 15;
    const targetTop = headerHeight + targetSlot * slotHeight - 20;
    container.scrollTop = Math.max(0, targetTop);
    verticalScrollDone.current = true;
  }, [appointments, startHour, slotHeight]);

  // Add scroll event listener
  useEffect(() => {
    const container = fullViewContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  /**
   * Smooth-scroll navigation by ~7 days
   */
  const handleSmoothScrollNav = (direction) => {
    const container = fullViewContainerRef.current;
    if (!container) return;

    const scrollAmount = dayColWidth * 7;
    const newScrollLeft = direction === 'prev'
      ? container.scrollLeft - scrollAmount
      : container.scrollLeft + scrollAmount;

    container.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth'
    });
  };

  /**
   * Calculate appointment height based on duration
   */
  const calculateAppointmentHeight = (appointment) => {
    const duration = appointment.estimated_duration || 60;
    const slots = Math.ceil(duration / 15);
    return slots * slotHeight - 2;
  };

  const formatHour = (hour, minute) => {
    if (minute !== 0) return '';
    const period = hour < 12 ? 'AM' : 'PM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour} ${period}`;
  };

  // ─── Drag & Drop (Desktop HTML5) ─────────────────────────────────────

  const handleDragStart = (e, appointment) => {
    setDraggedAppointment(appointment);
    draggedAppointmentRef.current = appointment;
    desktopDragRef.current = true;
    e.dataTransfer.effectAllowed = 'move';

    const rect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    setDragOffset(offsetY);
    dragOffsetRef.current = offsetY;

    const duration = appointment.estimated_duration || 60;
    const slots = Math.ceil(duration / 15);
    const fullHeight = slots * slotHeight;

    const dragElement = e.currentTarget.cloneNode(true);
    dragElement.style.opacity = '0.8';
    dragElement.style.transform = 'rotate(2deg)';
    dragElement.style.position = 'absolute';
    dragElement.style.top = '-1000px';
    dragElement.style.height = `${fullHeight}px`;
    dragElement.style.width = `${rect.width}px`;
    document.body.appendChild(dragElement);
    e.dataTransfer.setDragImage(dragElement, offsetX, offsetY);
    setDragImageElement(dragElement);

    setTimeout(() => {
      document.body.classList.add('calendar-drag-active');
    }, 0);
  };

  const handleDragEnd = () => {
    document.body.classList.remove('calendar-drag-active');
    if (dragImageElement && document.body.contains(dragImageElement)) {
      document.body.removeChild(dragImageElement);
    }
    setDragImageElement(null);
    desktopDragRef.current = false;
    draggedAppointmentRef.current = null;
    desktopDragSlotRef.current = null;
    setDraggedAppointment(null);
    if (dragPreviewRef.current) dragPreviewRef.current.style.display = 'none';
    if (dragLabelRef.current) dragLabelRef.current.style.display = 'none';
  };

  const handleDragOver = (e, day, hour, minute) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    const container = fullViewContainerRef.current;
    if (!container) return;

    const gridRect = container.getBoundingClientRect();

    // Auto-scroll near edges
    const scrollThreshold = 50;
    const scrollSpeed = 10;
    const mouseY = e.clientY - gridRect.top;
    const mouseX = e.clientX - gridRect.left;

    if (mouseY < scrollThreshold) {
      container.scrollTop = Math.max(0, container.scrollTop - scrollSpeed);
    } else if (mouseY > gridRect.height - scrollThreshold) {
      container.scrollTop += scrollSpeed;
    }
    if (mouseX < scrollThreshold) {
      container.scrollLeft = Math.max(0, container.scrollLeft - scrollSpeed);
    } else if (mouseX > gridRect.width - scrollThreshold) {
      container.scrollLeft += scrollSpeed;
    }

    // Calculate slot from Y position
    const cardTopY = e.clientY - dragOffset;
    const relativeY = cardTopY - gridRect.top + container.scrollTop;
    const dayHeaders = container.querySelector('.full-view-header');
    const headerHeight = dayHeaders ? dayHeaders.offsetHeight : 0;
    const adjustedY = relativeY - headerHeight;

    const fifteenMinSlotIndex = Math.floor(adjustedY / slotHeight);
    const totalFifteenMinSlots = (endHour - startHour) * 4;

    if (fifteenMinSlotIndex >= 0 && fifteenMinSlotIndex < totalFifteenMinSlots) {
      const targetHour = startHour + Math.floor(fifteenMinSlotIndex / 4);
      const targetMinute = (fifteenMinSlotIndex % 4) * 15;
      const slotKey = `${day.toISOString()}-${targetHour}-${targetMinute}`;
      if (desktopDragSlotRef.current !== slotKey) {
        desktopDragSlotRef.current = slotKey;
        // Update drag label
        if (dragLabelRef.current) {
          const h = targetHour > 12 ? targetHour - 12 : targetHour || 12;
          const ampm = targetHour >= 12 ? 'pm' : 'am';
          const m = targetMinute.toString().padStart(2, '0');
          const dayStr = day.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
          dragLabelRef.current.textContent = `${dayStr}, ${h}:${m}${ampm}`;
          dragLabelRef.current.style.display = 'block';
        }
      }
      if (dragLabelRef.current) {
        dragLabelRef.current.style.top = `${Math.max(10, e.clientY - 60)}px`;
        dragLabelRef.current.style.left = `${e.clientX}px`;
      }
    }
  };

  const handleDrop = (e, day, hour, minute) => {
    e.preventDefault();
    e.stopPropagation();

    const currentSlot = desktopDragSlotRef.current;
    if (draggedAppointment && onAppointmentDrop && currentSlot) {
      const lastDashIndex = currentSlot.lastIndexOf('-');
      const secondLastDashIndex = currentSlot.lastIndexOf('-', lastDashIndex - 1);
      const targetMinute = parseInt(currentSlot.substring(lastDashIndex + 1));
      const targetHour = parseInt(currentSlot.substring(secondLastDashIndex + 1, lastDashIndex));
      const dragDateISO = currentSlot.substring(0, secondLastDashIndex);

      const newDateTime = new Date(dragDateISO);
      newDateTime.setHours(targetHour, targetMinute, 0, 0);
      onAppointmentDrop(draggedAppointment, newDateTime);
    }

    setDraggedAppointment(null);
    if (dragPreviewRef.current) dragPreviewRef.current.style.display = 'none';
    if (dragLabelRef.current) dragLabelRef.current.style.display = 'none';
  };

  // ─── Resize handlers ─────────────────────────────────────────────────

  const resizeAutoScrollRef = useRef(null);
  const resizeTouchYRef = useRef(null); // current touch Y for auto-scroll + label positioning

  const startResize = (clientY, appointment, cardWrapper) => {
    const container = fullViewContainerRef.current;
    const duration = appointment.estimated_duration || 60;
    resizeRef.current = {
      startY: clientY,
      startScrollTop: container ? container.scrollTop : 0,
      startDuration: duration,
      currentDuration: duration,
      hasMoved: false,
    };
    resizeCardRef.current = cardWrapper;
    resizeTouchYRef.current = clientY;
    setResizingAppointment(appointment);
  };

  // Pointer-based resize start (desktop mouse)
  const handleResizeStart = (e, appointment, cardWrapper) => {
    e.stopPropagation();
    e.preventDefault();
    resizePointerRef.current = e.pointerId;
    resizeHandleRef.current = e.currentTarget;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    startResize(e.clientY, appointment, cardWrapper);
  };

  // Touch-based resize start (mobile) — 500ms long-press, cancelled by movement
  const resizeTouchPendingRef = useRef(null);

  const cancelResizePending = () => {
    if (resizeTouchPendingRef.current) {
      clearTimeout(resizeTouchPendingRef.current.timer);
      window.removeEventListener('touchmove', resizeTouchPendingRef.current.onMove);
      window.removeEventListener('touchend', resizeTouchPendingRef.current.onEnd);
      window.removeEventListener('touchcancel', resizeTouchPendingRef.current.onEnd);
      resizeTouchPendingRef.current = null;
    }
  };

  const handleResizeTouchStart = (e, appointment, cardWrapper) => {
    e.stopPropagation();
    if (e.touches.length !== 1) return;
    const touch = e.touches[0];
    const startX = touch.clientX;
    const startY = touch.clientY;

    cancelResizePending();

    const onMove = (ev) => {
      if (ev.touches.length !== 1) { cancelResizePending(); return; }
      const dx = ev.touches[0].clientX - startX;
      const dy = ev.touches[0].clientY - startY;
      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) cancelResizePending();
    };
    const onEnd = () => cancelResizePending();

    const timer = setTimeout(() => {
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
      resizeTouchPendingRef.current = null;
      if (navigator.vibrate) navigator.vibrate(50);
      startResize(startY, appointment, cardWrapper);
    }, 500);

    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd);
    window.addEventListener('touchcancel', onEnd);
    resizeTouchPendingRef.current = { timer, onMove, onEnd };
  };

  useEffect(() => {
    if (!resizingAppointment) return;
    const sh = slotHeightRef.current;

    const fmtDur = (m) => {
      if (m < 60) return `${m}m`;
      const h = Math.floor(m / 60);
      const rem = m % 60;
      return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
    };

    // Auto-scroll during resize when near container edges
    const startResizeAutoScroll = () => {
      const edgeZone = 120;
      const maxSpeed = 20;
      const tick = () => {
        const container = fullViewContainerRef.current;
        const y = resizeTouchYRef.current;
        if (!container || y == null || !resizingAppointment) return;
        const rect = container.getBoundingClientRect();
        const distFromTop = y - rect.top;
        const distFromBottom = rect.bottom - y;
        let scrolled = false;
        if (distFromTop < edgeZone && distFromTop > 0) {
          const amount = Math.ceil(maxSpeed * (1 - distFromTop / edgeZone));
          container.scrollTop = Math.max(0, container.scrollTop - amount);
          scrolled = true;
        } else if (distFromBottom < edgeZone && distFromBottom > 0) {
          const amount = Math.ceil(maxSpeed * (1 - distFromBottom / edgeZone));
          container.scrollTop += amount;
          scrolled = true;
        }
        // If we scrolled, recalculate resize since finger didn't move but content did
        if (scrolled && resizeTouchYRef.current != null) {
          onMove(resizeTouchYRef.current);
        }
        resizeAutoScrollRef.current = requestAnimationFrame(tick);
      };
      if (resizeAutoScrollRef.current) cancelAnimationFrame(resizeAutoScrollRef.current);
      resizeAutoScrollRef.current = requestAnimationFrame(tick);
    };

    const stopResizeAutoScroll = () => {
      if (resizeAutoScrollRef.current) {
        cancelAnimationFrame(resizeAutoScrollRef.current);
        resizeAutoScrollRef.current = null;
      }
    };

    const cleanup = () => {
      stopResizeAutoScroll();
      const r = resizeRef.current;
      resizeJustFinishedRef.current = true;
      setTimeout(() => { resizeJustFinishedRef.current = false; }, 300);

      if (r.currentDuration !== r.startDuration && onAppointmentDrop) {
        const currentTime = new Date(resizingAppointment.scheduled_date);
        onAppointmentDrop(resizingAppointment, currentTime, r.currentDuration);
      }
      if (resizeHandleRef.current && resizePointerRef.current != null) {
        try { resizeHandleRef.current.releasePointerCapture(resizePointerRef.current); } catch (_) {}
      }
      resizeCardRef.current = null;
      resizeHandleRef.current = null;
      resizePointerRef.current = null;
      resizeTouchYRef.current = null;
      setResizingAppointment(null);
    };

    const onMove = (clientY) => {
      const r = resizeRef.current;
      const container = fullViewContainerRef.current;
      const scrollDelta = container ? container.scrollTop - r.startScrollTop : 0;
      const deltaY = (clientY - r.startY) + scrollDelta;
      const slotsMoved = Math.round(deltaY / sh);
      const newDuration = Math.max(15, r.startDuration + slotsMoved * 15);
      r.currentDuration = newDuration;
      const newHeight = Math.ceil(newDuration / 15) * sh - 2;
      if (resizeCardRef.current) resizeCardRef.current.style.height = `${newHeight}px`;
      if (resizeLabelRef.current) {
        if (!r.hasMoved) {
          r.hasMoved = true;
          resizeLabelRef.current.style.display = 'block';
        }
        resizeLabelRef.current.textContent = fmtDur(newDuration);
        const wrapper = resizeCardRef.current;
        if (wrapper) {
          const wrapperRect = wrapper.getBoundingClientRect();
          resizeLabelRef.current.style.top = `${Math.max(10, clientY - 100)}px`;
          resizeLabelRef.current.style.left = `${wrapperRect.left + wrapperRect.width / 2}px`;
        }
      }
    };

    const onPointerMove = (e) => {
      resizeTouchYRef.current = e.clientY;
      onMove(e.clientY);
    };
    const onTouchMove = (e) => {
      e.preventDefault();
      if (e.touches.length === 1) {
        const y = e.touches[0].clientY;
        resizeTouchYRef.current = y;
        onMove(y);
      }
    };
    const onTouchEnd = () => cleanup();

    startResizeAutoScroll();

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', cleanup);
    window.addEventListener('pointercancel', cleanup);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('touchcancel', onTouchEnd);
    window.addEventListener('blur', cleanup);
    return () => {
      stopResizeAutoScroll();
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', cleanup);
      window.removeEventListener('pointercancel', cleanup);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
      window.removeEventListener('blur', cleanup);
    };
  }, [resizingAppointment, onAppointmentDrop]);

  // ─── Touch Drag (same pattern as WeekView) ──────────────────────────

  const getSlotFromY = (clientY, offsetFromCardTop = 0) => {
    const container = fullViewContainerRef.current;
    if (!container) return null;

    const gridRect = container.getBoundingClientRect();
    const cardTopY = clientY - offsetFromCardTop;
    const relativeY = cardTopY - gridRect.top + container.scrollTop;
    const dayHeaders = container.querySelector('.full-view-header');
    const headerHeight = dayHeaders ? dayHeaders.offsetHeight : 0;
    const adjustedY = relativeY - headerHeight;

    const sh = slotHeightRef.current;
    const sHour = startHourRef.current;
    const eHour = endHourRef.current;

    const fifteenMinSlotIndex = Math.floor(adjustedY / sh);
    const totalFifteenMinSlots = (eHour - sHour) * 4;

    if (fifteenMinSlotIndex >= 0 && fifteenMinSlotIndex < totalFifteenMinSlots) {
      const targetHour = sHour + Math.floor(fifteenMinSlotIndex / 4);
      const targetMinute = (fifteenMinSlotIndex % 4) * 15;
      return { targetHour, targetMinute, slotIndex: fifteenMinSlotIndex };
    }
    return null;
  };

  const getDayFromX = (clientX) => {
    const container = fullViewContainerRef.current;
    if (!container) return null;

    const headerGrid = container.querySelector('.full-view-header');
    if (!headerGrid) return null;

    const days = allDaysRef.current;
    const headerCells = headerGrid.children;
    // First child is the time column header
    for (let i = 1; i < headerCells.length; i++) {
      const rect = headerCells[i].getBoundingClientRect();
      if (clientX >= rect.left && clientX < rect.right) {
        return { dayIndex: i - 1, day: days[i - 1] };
      }
    }
    return null;
  };

  const cleanupTouchDrag = () => {
    const state = touchDragRef.current;
    if (state) {
      if (state.longPressTimer) clearTimeout(state.longPressTimer);
      if (state.ghostElement && document.body.contains(state.ghostElement)) {
        document.body.removeChild(state.ghostElement);
      }
    }
    if (autoScrollRef.current) {
      cancelAnimationFrame(autoScrollRef.current);
      autoScrollRef.current = null;
    }
    document.body.classList.remove('is-touch-dragging');
    document.body.classList.remove('calendar-drag-active');
    const container = fullViewContainerRef.current;
    if (container) container.style.touchAction = '';

    touchDragRef.current = null;
    touchDragSlotRef.current = null;
    setDraggedAppointment(null);
    // Hide the drag preview and label
    if (dragPreviewRef.current) dragPreviewRef.current.style.display = 'none';
    if (dragLabelRef.current) dragLabelRef.current.style.display = 'none';
  };

  const startAutoScroll = () => {
    const edgeZone = 150;
    const maxSpeed = 30;

    const tick = () => {
      if (!touchDragRef.current || !touchDragRef.current.activated) return;
      const container = fullViewContainerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const x = touchDragRef.current.currentX;
      const y = touchDragRef.current.currentY;

      const distFromTop = y - rect.top;
      const distFromBottom = rect.bottom - y;
      if (distFromTop < edgeZone && distFromTop > 0) {
        container.scrollTop = Math.max(0, container.scrollTop - Math.ceil(maxSpeed * (1 - distFromTop / edgeZone)));
      } else if (distFromBottom < edgeZone && distFromBottom > 0) {
        container.scrollTop += Math.ceil(maxSpeed * (1 - distFromBottom / edgeZone));
      }

      const distFromLeft = x - rect.left;
      const distFromRight = rect.right - x;
      if (distFromLeft < edgeZone && distFromLeft > 0) {
        container.scrollLeft = Math.max(0, container.scrollLeft - Math.ceil(maxSpeed * (1 - distFromLeft / edgeZone)));
      } else if (distFromRight < edgeZone && distFromRight > 0) {
        container.scrollLeft += Math.ceil(maxSpeed * (1 - distFromRight / edgeZone));
      }

      autoScrollRef.current = requestAnimationFrame(tick);
    };

    if (autoScrollRef.current) cancelAnimationFrame(autoScrollRef.current);
    autoScrollRef.current = requestAnimationFrame(tick);
  };

  /**
   * Position the drag preview element via direct DOM manipulation.
   * This avoids React re-renders during drag — only the preview's style changes.
   */
  const updateDragPreview = (dayIndex, targetHour, targetMinute, appointment, clientX) => {
    const preview = dragPreviewRef.current;
    if (!preview) return;

    const container = fullViewContainerRef.current;
    if (!container) return;

    const sh = slotHeightRef.current;
    const sHour = startHourRef.current;
    const tcw = timeColWidthRef.current;
    const widths = dayColumnWidthsRef.current;
    const offsets = dayColumnOffsetsRef.current;
    const days = allDaysRef.current;

    if (!widths || !offsets || !days || dayIndex < 0 || dayIndex >= days.length) {
      preview.style.display = 'none';
      return;
    }

    // Calculate top position: header height + slot offset
    const headerEl = container.querySelector('.full-view-header');
    const headerHeight = headerEl ? headerEl.offsetHeight : 0;
    const totalMinutesFromStart = (targetHour - sHour) * 60 + targetMinute;
    const topPx = headerHeight + (totalMinutesFromStart / 15) * sh;

    // Calculate left position from day column offsets
    const dayLeftPx = tcw + offsets[dayIndex];
    const baseWidth = dayColWidthRef.current || 120;
    const dayTotalWidth = widths[dayIndex];
    const subCols = Math.round(dayTotalWidth / baseWidth) || 1;

    // If the day has multiple sub-columns, snap to whichever the cursor is over
    let subColIndex = 0;
    if (subCols > 1 && clientX != null) {
      const containerRect = container.getBoundingClientRect();
      const pxIntoDay = (clientX - containerRect.left + container.scrollLeft) - (tcw + offsets[dayIndex]);
      subColIndex = Math.max(0, Math.min(subCols - 1, Math.floor(pxIntoDay / baseWidth)));
    }

    const dragDuration = appointment.estimated_duration || 60;
    const previewLeft = dayLeftPx + subColIndex * baseWidth;
    const previewWidth = baseWidth;
    const previewHeight = Math.ceil(dragDuration / 15) * sh - 2;

    preview.style.display = 'block';
    preview.style.top = `${topPx}px`;
    preview.style.left = `${previewLeft}px`;
    preview.style.width = `${previewWidth}px`;
    preview.style.height = `${previewHeight}px`;

    // Update drag label with date + time
    if (dragLabelRef.current) {
      const day = days[dayIndex];
      const h = targetHour > 12 ? targetHour - 12 : targetHour || 12;
      const ampm = targetHour >= 12 ? 'pm' : 'am';
      const m = targetMinute.toString().padStart(2, '0');
      const dayStr = day.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
      dragLabelRef.current.textContent = `${dayStr}, ${h}:${m}${ampm}`;
      dragLabelRef.current.style.display = 'block';
      // Position above finger
      const state = touchDragRef.current;
      if (state) {
        dragLabelRef.current.style.top = `${Math.max(10, state.currentY - 100)}px`;
        const containerRect = container.getBoundingClientRect();
        const cardCenterX = containerRect.left - container.scrollLeft + previewLeft + previewWidth / 2;
        dragLabelRef.current.style.left = `${cardCenterX}px`;
      }
    }
  };

  /**
   * Unified pointer drag: handles BOTH touch and mouse drag.
   * - Touch: long-press (300ms) to activate
   * - Mouse: activate after 5px movement (no long-press needed)
   */
  const handlePointerDown = (e, appointment, cardElement) => {
    if (e.target.closest('button') || e.target.closest('[data-resize-handle]')) return;
    // On touch, reject if pointer is in the bottom resize zone of the card
    if (e.pointerType === 'touch' && cardElement) {
      const rect = cardElement.getBoundingClientRect();
      if (e.clientY > rect.bottom - 40) return;
    }

    const isTouch = e.pointerType === 'touch';
    const startX = e.clientX;
    const startY = e.clientY;

    const longPressTimer = isTouch ? setTimeout(() => {
      if (!touchDragRef.current) return;
      touchDragRef.current.activated = true;
      if (navigator.vibrate) navigator.vibrate(50);

      const container = fullViewContainerRef.current;
      if (container) container.style.touchAction = 'none';

      document.body.classList.add('is-touch-dragging');
      setDraggedAppointment(appointment);

      const rect = cardElement.getBoundingClientRect();
      touchDragRef.current.offsetX = startX - rect.left;
      touchDragRef.current.offsetY = startY - rect.top;
      startAutoScroll();
    }, 300) : null;

    touchDragRef.current = {
      appointment,
      cardElement,
      startX,
      startY,
      currentX: startX,
      currentY: startY,
      activated: false,
      longPressTimer,
      ghostElement: null,
      offsetX: 0,
      offsetY: 0,
      pointerType: e.pointerType
    };
  };

  // Mount-once pointer event listeners for touch drag
  useEffect(() => {
    const onTouchMove = (e) => {
      const state = touchDragRef.current;
      if (!state) return;
      if (state.activated) {
        e.preventDefault();
      } else {
        const touch = e.touches[0];
        const dx = touch.clientX - state.startX;
        const dy = touch.clientY - state.startY;
        if (Math.abs(dx) <= 5 && Math.abs(dy) <= 5) {
          e.preventDefault();
        }
      }
    };

    const onPointerMove = (e) => {
      const state = touchDragRef.current;
      if (!state) return;
      const dx = e.clientX - state.startX;
      const dy = e.clientY - state.startY;

      if (!state.activated) {
        if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
          if (state.longPressTimer) clearTimeout(state.longPressTimer);

          if (state.pointerType === 'mouse') {
            // Mouse: activate drag immediately on 5px movement
            state.activated = true;

            const container = fullViewContainerRef.current;
            if (container) container.style.touchAction = 'none';

            document.body.classList.add('is-touch-dragging');
            document.body.classList.add('calendar-drag-active');
            setDraggedAppointment(state.appointment);

            const rect = state.cardElement.getBoundingClientRect();
            state.offsetX = state.startX - rect.left;
            state.offsetY = state.startY - rect.top;

            startAutoScroll();
            // Fall through to the activated movement handling below
          } else {
            // Touch: cancel — finger moved before long-press completed
            touchDragRef.current = null;
            return;
          }
        } else {
          return;
        }
      }

      e.preventDefault();
      state.currentX = e.clientX;
      state.currentY = e.clientY;

      const slotResult = getSlotFromY(e.clientY, state.offsetY);
      const dayResult = getDayFromX(e.clientX);

      if (slotResult && dayResult) {
        const slotKey = `${dayResult.day.toISOString()}-${slotResult.targetHour}-${slotResult.targetMinute}`;
        touchDragSlotRef.current = slotKey;
        // Position preview via direct DOM manipulation — no React re-render
        // Always update: even within the same time slot the sub-column can change
        {
          updateDragPreview(dayResult.dayIndex, slotResult.targetHour, slotResult.targetMinute, state.appointment, e.clientX);
        }
      }
    };

    const onPointerUp = () => {
      const state = touchDragRef.current;
      if (!state) return;

      if (state.activated && onAppointmentDropRef.current) {
        const currentSlot = touchDragSlotRef.current;
        if (currentSlot) {
          const lastDashIndex = currentSlot.lastIndexOf('-');
          const secondLastDashIndex = currentSlot.lastIndexOf('-', lastDashIndex - 1);
          const targetMinute = parseInt(currentSlot.substring(lastDashIndex + 1));
          const targetHour = parseInt(currentSlot.substring(secondLastDashIndex + 1, lastDashIndex));
          const dragDateISO = currentSlot.substring(0, secondLastDashIndex);

          const newDateTime = new Date(dragDateISO);
          newDateTime.setHours(targetHour, targetMinute, 0, 0);
          onAppointmentDropRef.current(state.appointment, newDateTime);
        }
      }
      cleanupTouchDrag();
    };

    const onPointerCancel = () => {
      cleanupTouchDrag();
    };

    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('pointermove', onPointerMove, { passive: false });
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerCancel);

    return () => {
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerCancel);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => cleanupTouchDrag();
  }, []);

  // (HTML5 DnD listeners removed — pointer events handle both mouse and touch)

  // Close overflow popover on click outside or Escape
  useEffect(() => {
    if (!overflowPopover) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.overflow-popover')) setOverflowPopover(null);
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setOverflowPopover(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [overflowPopover]);

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full">
      <style>{`
        body.calendar-drag-active .appointment-grid {
          z-index: 3 !important;
        }
        body.is-touch-dragging {
          user-select: none;
          -webkit-user-select: none;
        }
        body.is-touch-dragging .full-view-scroll-container {
          touch-action: none;
        }
        .full-view-scroll-container {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .full-view-scroll-container::-webkit-scrollbar {
          display: none;
        }
        .full-view-time-col {
          position: sticky;
          left: 0;
          z-index: 25;
          background: #f9fafb;
          box-shadow: 0 -3px 0 0 #f9fafb, 0 3px 0 0 #f9fafb;
        }
        .full-view-time-header {
          position: sticky;
          left: 0;
          z-index: 30;
          background: white;
          box-shadow: 0 -3px 0 0 white, 0 3px 0 0 white;
        }
      `}</style>

      {/* Navigation + Controls */}
      <div className="flex items-center justify-between px-4 py-1.5">
        <div className="flex items-center space-x-1">
          <button
            onClick={() => handleSmoothScrollNav('prev')}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            title="Scroll back 7 days"
          >
            <ChevronLeft size={20} className="text-gray-600" />
          </button>
          <h2 ref={visibleDateRangeRef} className="text-lg font-bold text-pest-blue mx-1">
            Full View
          </h2>
          <button
            onClick={() => handleSmoothScrollNav('next')}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            title="Scroll forward 7 days"
          >
            <ChevronRight size={20} className="text-gray-600" />
          </button>
        </div>

        {/* Zoom controls */}
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-1">
            <button
              onClick={() => handleZoomOut(null, null)}
              disabled={zoomLevel === 0.5}
              className="p-1 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              title="Zoom out (Ctrl + -)"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
              </svg>
            </button>
            <button
              onClick={() => handleResetZoom(null, null)}
              className="px-1.5 py-0.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded transition-colors text-xs font-medium"
              title="Reset zoom (Ctrl + 0)"
            >
              {Math.round(zoomLevel * 100)}%
            </button>
            <button
              onClick={() => handleZoomIn(null, null)}
              disabled={zoomLevel === 2}
              className="p-1 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              title="Zoom in (Ctrl + +)"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable container */}
      <div ref={fullViewContainerRef} className="flex-1 overflow-auto full-view-scroll-container">
        <div style={{ width: `${contentWidth}px`, position: 'relative' }}>

          {/* Day headers - sticky top */}
          <div
            className="full-view-header flex border-b-2 border-gray-300 sticky top-0 bg-white shadow-sm"
            style={{ zIndex: 20, width: `${contentWidth}px` }}
          >
            {/* Time column header */}
            <div
              className="full-view-time-header px-2 py-1.5 font-semibold text-gray-500 text-xs border-r border-gray-200 flex items-center justify-center flex-shrink-0"
              style={{ width: `${timeColWidth}px` }}
            />
            {/* Day column headers */}
            {allDays.map((day, dayIndex) => {
              const isTodayDay = isToday(day);
              const isCurrent = isCurrentDate(day);
              const isWeekend = day.getDay() === 0 || day.getDay() === 6;
              const dateKey = day.toISOString().split('T')[0];

              return (
                <div
                  key={dateKey}
                  className={`px-1 py-1.5 text-center border-r border-gray-200 flex-shrink-0 ${
                    isTodayDay ? 'bg-blue-50' : isWeekend ? 'bg-gray-50' : ''
                  } ${isCurrent ? 'ring-2 ring-pest-blue ring-inset' : ''}`}
                  style={{ width: `${dayColumnWidths[dayIndex]}px` }}
                >
                  <span className="text-xs text-gray-500 font-medium mr-1">
                    {day.toLocaleDateString('en-US', { weekday: 'short' })}
                  </span>
                  <span className={`text-sm font-bold ${
                    isTodayDay ? 'text-pest-blue' : 'text-gray-700'
                  }`}>
                    {day.getDate()}
                  </span>
                  {(day.getDate() === 1 || dayIndex === 0) && (
                    <span className="text-xs text-gray-400 ml-1">
                      {day.toLocaleDateString('en-US', { month: 'short' })}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Time slots (15-min rows) — grid lines in a z-index:-1 layer so cards always paint on top */}
          <div style={{ position: 'relative', isolation: 'isolate' }}>
          {/* Grid lines layer — paints below all content at z-index:-1 */}
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: -1,
              pointerEvents: 'none',
              backgroundImage: `
                repeating-linear-gradient(to bottom,
                  transparent 0px,
                  transparent ${slotHeight - 1}px,
                  #f3f4f6 ${slotHeight - 1}px,
                  #f3f4f6 ${slotHeight}px
                ),
                repeating-linear-gradient(to bottom,
                  transparent 0px,
                  transparent ${slotHeight * 4 - 2}px,
                  #d1d5db ${slotHeight * 4 - 2}px,
                  #d1d5db ${slotHeight * 4}px
                )
              `
            }}
          />
          {timeSlots.map(({ hour, minute }) => {
            const isHourStart = minute === 0;
            const hourLabel = formatHour(hour, minute);

            return (
              <div
                key={`${hour}-${minute}`}
                className="flex"
                style={{ height: `${slotHeight}px`, overflow: 'visible' }}
              >
                {/* Time label — sticky left */}
                <div
                  className={`full-view-time-col p-1 text-xs text-gray-600 font-medium border-r border-gray-200 flex items-center justify-center flex-shrink-0 ${!hourLabel ? 'bg-gray-25' : ''}`}
                  style={{ width: `${timeColWidth}px` }}
                >
                  {hourLabel}
                </div>

                {/* Day columns */}
                {allDays.map((day, dayIndex) => {
                  const dateStr = day.toISOString().split('T')[0];
                  const isWeekend = day.getDay() === 0 || day.getDay() === 6;

                  const startingAppointments = appointmentsBySlot.get(`${dateStr}-${hour}-${minute}`) || [];

                  const slotKey = `${day.toISOString()}-${hour}-${minute}`;
                  const slotInfo = calculateOverlapColumns.get(slotKey) || { columns: 1, appointmentColumns: new Map() };
                  const isTodayCell = isToday(day);

                  return (
                    <div
                      key={dateStr}
                      className={`border-r border-gray-200 relative flex-shrink-0 ${
                        isTodayCell ? 'bg-blue-25' : isWeekend ? 'bg-gray-50/50' : ''
                      }`}
                      style={{ width: `${dayColumnWidths[dayIndex]}px`, cursor: draggedAppointment ? 'copy' : 'pointer' }}
                    >
                      {/* Click zone for adding appointments */}
                      {!draggedAppointment && (
                        <div
                          className="absolute inset-0 hover:bg-gray-50 cursor-pointer"
                          onClick={() => {
                            const slotTime = new Date(day);
                            slotTime.setHours(hour, minute, 0, 0);
                            onTimeSlotClick(slotTime);
                          }}
                        />
                      )}

                      {/* Appointment cards */}
                      {startingAppointments.length > 0 && (() => {
                        const maxColsForGrid = Math.max(
                          1,
                          ...startingAppointments.map(apt => {
                            const info = globalColumnLayout.get(apt.id);
                            return info ? info.totalCols : 1;
                          })
                        );

                        const visibleAppointments = startingAppointments;
                        const effectiveCols = maxColsForGrid;

                        return (
                          <div
                            className={`grid gap-0.5 px-1 absolute top-0 left-0 appointment-grid ${effectiveCols > 1 ? 'right-0' : ''}`}
                            style={{
                              gridTemplateColumns: effectiveCols > 1
                                ? `repeat(${effectiveCols}, 1fr)`
                                : `minmax(0, ${dayColumnWidths[dayIndex]}px)`,
                              height: `${Math.max(...visibleAppointments.map(apt => calculateAppointmentHeight(apt)))}px`,
                              zIndex: 10,
                              pointerEvents: 'none',
                              overflow: 'visible'
                            }}
                          >
                            {visibleAppointments.map((apt, visIdx) => {
                              const columnIndex = (globalColumnLayout.get(apt.id) || {}).colIndex ?? 0;
                              const height = calculateAppointmentHeight(apt);

                              return (
                                <div
                                  key={apt.id}
                                  data-apt-wrapper="true"
                                  style={{
                                    gridColumn: `${columnIndex + 1} / span 1`,
                                    gridRow: '1',
                                    height: `${height}px`,
                                    position: 'relative',
                                    zIndex: 10,
                                    pointerEvents: draggedAppointment ? 'none' : 'auto'
                                  }}
                                >
                                  <AppointmentCard
                                    appointment={apt}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (resizeJustFinishedRef.current) return;
                                      onAppointmentClick(apt);
                                    }}
                                    onPointerDown={(e) => handlePointerDown(e, apt, e.currentTarget)}
                                    onQuickStatus={onQuickStatus}
                                    onResizeStart={(e) => handleResizeStart(e, apt, e.currentTarget.closest('[data-apt-wrapper]'))}
                                    onResizeTouchStart={(e) => handleResizeTouchStart(e, apt, e.currentTarget.closest('[data-apt-wrapper]'))}
                                    isResizing={resizingAppointment && resizingAppointment.id === apt.id}
                                    style={{
                                      height: '100%',
                                      opacity: (draggedAppointment && draggedAppointment.id === apt.id) ? 0.15
                                        : (resizingAppointment && resizingAppointment.id === apt.id) ? 0.6
                                        : 1
                                    }}
                                    draggable={false}
                                    minimal={true}
                                  />

                                </div>
                              );
                            })}

                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            );
          })}
          </div>

          {/* Drag preview — single element positioned via ref (zero re-renders during drag) */}
          {draggedAppointment && (
            <div
              ref={dragPreviewRef}
              className="absolute pointer-events-none"
              style={{ display: 'none', zIndex: 15 }}
            >
              <AppointmentCard
                appointment={draggedAppointment}
                style={{ height: '100%', opacity: 0.5, filter: 'brightness(1.1)' }}
                draggable={false}
                minimal={true}
              />
            </div>
          )}
        </div>
      </div>

      {/* Overflow Popover */}
      {overflowPopover && ReactDOM.createPortal(
        <div
          className="overflow-popover fixed bg-white rounded-lg shadow-2xl border border-gray-200 p-3 z-[99999] max-w-xs animate-fadeIn"
          style={{
            top: `${Math.min(overflowPopover.anchorRect.top, window.innerHeight - 200)}px`,
            left: `${Math.min(overflowPopover.anchorRect.left, window.innerWidth - 280)}px`
          }}
        >
          <div className="text-xs font-semibold text-gray-500 mb-2">
            {overflowPopover.appointments.length} more appointment{overflowPopover.appointments.length !== 1 ? 's' : ''}
          </div>
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {overflowPopover.appointments.map(apt => {
              const time = new Date(apt.scheduled_date).toLocaleTimeString('en-US', {
                hour: 'numeric', minute: '2-digit', hour12: true
              });
              const jobTitle = apt.job_title
                ? apt.job_title.split('>').pop().trim().split(' - ')[0].trim()
                : 'Untitled';

              return (
                <button
                  key={apt.id}
                  className="w-full text-left px-2 py-1.5 rounded hover:bg-gray-100 transition-colors flex items-center space-x-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOverflowPopover(null);
                    onAppointmentClick(apt);
                  }}
                >
                  <Clock size={12} className="text-gray-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-gray-900 truncate">{jobTitle}</div>
                    <div className="text-xs text-gray-500">{time} {apt.customer_name ? `- ${apt.customer_name}` : ''}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}
      {/* Drag date/time label — rendered as portal so it floats above everything */}
      {draggedAppointment && ReactDOM.createPortal(
        <div
          ref={dragLabelRef}
          className="fixed bg-blue-600 text-white text-sm font-semibold px-3 py-1.5 rounded-lg pointer-events-none shadow-lg whitespace-nowrap"
          style={{ zIndex: 99999, top: '-100px', left: '-100px', transform: 'translateX(-50%)', display: 'none' }}
        />,
        document.body
      )}

      {/* Resize duration label — rendered as portal so it floats above everything */}
      {resizingAppointment && ReactDOM.createPortal(
        <div
          ref={resizeLabelRef}
          className="fixed bg-blue-600 text-white text-sm font-semibold px-3 py-1.5 rounded-lg pointer-events-none shadow-lg whitespace-nowrap"
          style={{ zIndex: 99999, top: '-100px', left: '-100px', transform: 'translateX(-50%)', display: 'none' }}
        >
          {(() => { const m = resizingAppointment.estimated_duration || 60; if (m < 60) return `${m}m`; const h = Math.floor(m/60); const r = m%60; return r > 0 ? `${h}h ${r}m` : `${h}h`; })()}
        </div>,
        document.body
      )}
    </div>
  );
};

export default FullView2;
